import { useCallback, useEffect, useMemo, useState } from "react";
import type { TenantPermissions } from "@/features/auth/lib/api";

const STORAGE_KEY = "nexus.tenantSelector.recent";
// Keep up to 4 tenants: the active tenant always occupies the most-recent
// position and is shown in the list, so 4 stored ⇒ active tenant plus up to 3 others.
const MAX_RECENT = 4;

function readStorage(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === "string").slice(0, MAX_RECENT);
  } catch {
    return [];
  }
}

function writeStorage(ids: string[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // swallow — quota/private-mode errors shouldn't break selector
  }
}

export interface UseRecentTenantsResult {
  /** Recent tenants (most recent first), including the currently active one. */
  recent: TenantPermissions[];
  /** Record a tenant as recently selected. */
  record: (tenantId: string) => void;
}

/**
 * Track recently selected tenants in `localStorage`.
 * Prunes ids that are no longer in the user's tenant list.
 */
export function useRecentTenants(
  tenants: TenantPermissions[],
  activeTenantId: string | null,
): UseRecentTenantsResult {
  const [ids, setIds] = useState<string[]>(() => readStorage());

  const validTenantIds = useMemo(
    () => new Set(tenants.map((t) => t.id)),
    [tenants],
  );

  useEffect(() => {
    const pruned = ids.filter((id) => validTenantIds.has(id));
    if (pruned.length !== ids.length) {
      setIds(pruned);
      writeStorage(pruned);
    }
  }, [ids, validTenantIds]);

  const record = useCallback((tenantId: string) => {
    setIds((prev) => {
      if (prev[0] === tenantId) return prev;
      const next = [tenantId, ...prev.filter((id) => id !== tenantId)].slice(
        0,
        MAX_RECENT,
      );
      writeStorage(next);
      return next;
    });
  }, []);

  // Record the active tenant whenever it changes (including the one active at
  // mount). Recording only on explicit click left the just-switched-from tenant
  // — and the page-load tenant — out of history, so "Recent" lagged a switch
  // behind and looked inconsistent. Driving it off `activeTenantId` keeps the
  // history accurate the instant a switch lands.
  useEffect(() => {
    if (!activeTenantId || !validTenantIds.has(activeTenantId)) return;
    record(activeTenantId);
  }, [activeTenantId, validTenantIds, record]);

  const recent = useMemo(() => {
    const byId = new Map(tenants.map((t) => [t.id, t]));
    return ids
      .map((id) => byId.get(id))
      .filter((t): t is TenantPermissions => Boolean(t));
  }, [ids, tenants]);

  return { recent, record };
}
